import type {
  CalculatedBreakdown,
  PaymentAllocation,
} from '@/api/rebates/types';
import { sortDatesChronologically } from './breakdown-transforms/timeFilter';

export type AllocationMode = 'waterfall' | 'distribute-evenly';

export type AllocationState = Record<string, number>; // Key: `${ruleId}|${divisionId}|${date}`, Value: allocated amount

export interface AllocationBucket {
  key: string; // `${ruleId}|${divisionId}|${date}`
  remainingCapacity: number; // Max amount that can be allocated (rebateAmount - existingAllocation)
  rebateAmount: number; // Expected rebate amount from breakdown
  existingAllocation: number; // Already allocated amount (excluding current payment)
}

/**
 * Builds a map of existing allocations excluding the current payment
 * Key format: `${ruleId}|${divisionId}|${date}`
 */
export function buildExistingAllocationsMap(
  globalAllocations: PaymentAllocation[],
  currentPaymentId: string | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  globalAllocations
    .filter((alloc) => alloc.rebateSupplierPaymentId !== currentPaymentId)
    .forEach((alloc) => {
      const key = `${alloc.rebateRuleId}|${alloc.divisionId || 'null'}|${alloc.date}`;
      const existing = map.get(key) || 0;
      map.set(key, existing + alloc.allocatedAmount);
    });
  return map;
}

/**
 * Calculates remaining capacity for each breakdown
 * Returns buckets with their capacity information
 */
export function calculateBucketsWithCapacity(
  breakdowns: CalculatedBreakdown[],
  existingAllocationsMap: Map<string, number>,
): AllocationBucket[] {
  return breakdowns.map((bd) => {
    const key = `${bd.ruleId}|${bd.divisionId}|${bd.date}`;
    const existingAllocation = existingAllocationsMap.get(key) || 0;
    const remainingCapacity = Math.max(0, bd.rebateAmount - existingAllocation);
    return {
      key,
      remainingCapacity,
      rebateAmount: bd.rebateAmount,
      existingAllocation,
    };
  });
}

/**
 * Normalizes allocations to ensure total matches expected amount exactly
 * Adjusts the largest allocation that can accommodate the difference
 */
export function normalizeAllocations(
  allocations: Record<string, number>,
  expectedTotal: number,
  buckets: AllocationBucket[],
): Record<string, number> {
  const currentTotal = Object.values(allocations).reduce(
    (sum, amt) => sum + amt,
    0,
  );
  const difference = expectedTotal - currentTotal;

  // If there's a small difference due to rounding, adjust the largest allocation
  if (Math.abs(difference) > 0.001 && Math.abs(difference) < 0.01) {
    // Find the largest allocation that can be adjusted
    const sortedEntries = Object.entries(allocations)
      .map(([key, amount]) => {
        const bucket = buckets.find((b) => b.key === key);
        const remainingCapacity = bucket
          ? Math.max(0, bucket.remainingCapacity - amount)
          : 0;
        return { key, amount, remainingCapacity };
      })
      .sort((a, b) => b.amount - a.amount);

    for (const entry of sortedEntries) {
      if (entry.remainingCapacity >= Math.abs(difference)) {
        const adjustment = Math.min(difference, entry.remainingCapacity);
        const newAmount = parseFloat((entry.amount + adjustment).toFixed(2));
        allocations[entry.key] = newAmount;
        break;
      }
    }
  }

  return allocations;
}

/**
 * Distributes allocations across buckets using the specified mode
 *
 * @param buckets - Array of allocation buckets with capacity information
 * @param amountToAllocate - Total amount to allocate
 * @param mode - 'waterfall' (sequential) or 'distribute-evenly' (proportional)
 * @returns Record of allocations keyed by bucket key
 */
export function distributeAllocations(
  buckets: AllocationBucket[],
  amountToAllocate: number,
  mode: AllocationMode,
): Record<string, number> {
  const allocations: Record<string, number> = {};
  let remainingAmount = amountToAllocate;

  // Filter out buckets with no capacity
  const eligibleBuckets = buckets.filter((b) => b.remainingCapacity > 0);

  if (eligibleBuckets.length === 0) {
    return allocations;
  }

  if (mode === 'waterfall') {
    // Waterfall: allocate sequentially, fully allocating each bucket before moving to next
    for (const bucket of eligibleBuckets) {
      if (remainingAmount <= 0.01) break;

      const amountToAllocateToBucket = Math.min(
        remainingAmount,
        bucket.remainingCapacity,
      );
      const roundedAmount = parseFloat(amountToAllocateToBucket.toFixed(2));
      allocations[bucket.key] = roundedAmount;
      remainingAmount -= roundedAmount;
    }
  } else {
    // Distribute-evenly: divide amount proportionally, use modulo for remainder
    // First, calculate total remaining capacity
    const totalCapacity = eligibleBuckets.reduce(
      (sum, b) => sum + b.remainingCapacity,
      0,
    );

    if (totalCapacity === 0) {
      return allocations;
    }

    // Distribute proportionally based on remaining capacity
    const allocationsWithRemainder: Array<{
      bucket: AllocationBucket;
      baseAmount: number;
      remainder: number;
    }> = [];

    let totalBaseAmount = 0;

    // First pass: calculate base amounts and track remainder
    eligibleBuckets.forEach((bucket) => {
      const proportion = bucket.remainingCapacity / totalCapacity;
      const baseAmount = amountToAllocate * proportion;
      // Round down to 2 decimals for base amount
      const baseAmountRounded = Math.floor(baseAmount * 100) / 100;
      const remainder = baseAmount - baseAmountRounded;

      allocationsWithRemainder.push({
        bucket,
        baseAmount: baseAmountRounded,
        remainder,
      });
      totalBaseAmount += baseAmountRounded;
    });

    // Calculate total remainder (amount not yet allocated due to rounding down)
    const totalRemainder = amountToAllocate - totalBaseAmount;

    // Second pass: distribute remainder using modulo math
    // Sort by remainder (largest first) to distribute cents fairly
    const sortedByRemainder = [...allocationsWithRemainder].sort(
      (a, b) => b.remainder - a.remainder,
    );

    // Distribute remainder cents one at a time, respecting capacity limits
    let remainderToDistribute = Math.round(totalRemainder * 100); // Convert to cents

    for (const item of sortedByRemainder) {
      if (remainderToDistribute <= 0) break;

      const currentAllocation = item.baseAmount;
      const maxAllocation = item.bucket.remainingCapacity;
      const canAddOneCent = currentAllocation + 0.01 <= maxAllocation;

      if (canAddOneCent) {
        item.baseAmount = parseFloat((currentAllocation + 0.01).toFixed(2));
        remainderToDistribute -= 1;
      }
    }

    // Final pass: set allocations, capping at remaining capacity
    allocationsWithRemainder.forEach((item) => {
      const finalAmount = Math.min(
        item.baseAmount,
        item.bucket.remainingCapacity,
      );
      const roundedAmount = parseFloat(finalAmount.toFixed(2));
      allocations[item.bucket.key] = roundedAmount;
      remainingAmount -= roundedAmount;
    });
  }

  return allocations;
}

export interface AllocateParams {
  calculatedBreakdowns: CalculatedBreakdown[];
  dates: string[];
  availableAmount: number;
  globalAllocations: PaymentAllocation[];
  paymentId: string;
}

export interface AllocateByRuleParams extends AllocateParams {
  selectedRuleIds: string[];
}

export interface AllocateByDivisionParams extends AllocateParams {
  selectedDivisionIds: string[];
}

/**
 * Auto-allocate: distribute payment proportionally based on expected rebate amounts
 * Accounts for existing allocations and caps each allocation at remaining capacity
 * Respects the selected date range
 */
export function allocateAuto(params: AllocateParams): AllocationState {
  const {
    calculatedBreakdowns,
    dates,
    availableAmount,
    globalAllocations,
    paymentId,
  } = params;

  // Reset allocations first
  const newAllocations: AllocationState = {};

  if (calculatedBreakdowns.length === 0 || dates.length === 0) {
    return newAllocations;
  }

  // Filter breakdowns to only include dates in the selected range
  const dateSet = new Set(dates);
  const filteredBreakdowns = calculatedBreakdowns.filter((bd) =>
    dateSet.has(bd.date),
  );

  if (filteredBreakdowns.length === 0) {
    return newAllocations;
  }

  // Build map of existing allocations (excluding current payment)
  const existingAllocationsMap = buildExistingAllocationsMap(
    globalAllocations,
    paymentId,
  );

  // Calculate buckets with capacity
  const buckets = calculateBucketsWithCapacity(
    filteredBreakdowns,
    existingAllocationsMap,
  );

  // Check if there's any capacity
  const totalRemainingCapacity = buckets.reduce(
    (sum, b) => sum + b.remainingCapacity,
    0,
  );

  if (totalRemainingCapacity === 0) {
    return newAllocations;
  }

  // Distribute evenly across all buckets
  const distributedAllocations = distributeAllocations(
    buckets,
    availableAmount,
    'distribute-evenly',
  );

  // Normalize to fix any rounding differences
  const normalizedAllocations = normalizeAllocations(
    distributedAllocations,
    availableAmount,
    buckets,
  );

  return normalizedAllocations;
}

/**
 * Allocate by Month: waterfall chronologically through months
 * Fully allocates each month before moving to the next
 */
export function allocateByMonth(params: AllocateParams): AllocationState {
  const {
    calculatedBreakdowns,
    dates,
    availableAmount,
    globalAllocations,
    paymentId,
  } = params;

  // Reset allocations first
  const newAllocations: AllocationState = {};

  if (calculatedBreakdowns.length === 0 || dates.length === 0) {
    return newAllocations;
  }

  // Filter breakdowns to only include dates in the selected range
  const dateSet = new Set(dates);
  const filteredBreakdowns = calculatedBreakdowns.filter((bd) =>
    dateSet.has(bd.date),
  );

  if (filteredBreakdowns.length === 0) {
    return newAllocations;
  }

  // Build map of existing allocations (excluding current payment)
  const existingAllocationsMap = buildExistingAllocationsMap(
    globalAllocations,
    paymentId,
  );

  // Group breakdowns by month (date)
  const breakdownsByDate = new Map<string, typeof filteredBreakdowns>();
  filteredBreakdowns.forEach((bd) => {
    if (!breakdownsByDate.has(bd.date)) {
      breakdownsByDate.set(bd.date, []);
    }
    breakdownsByDate.get(bd.date)!.push(bd);
  });

  // Sort dates chronologically
  const sortedDates = sortDatesChronologically(
    Array.from(breakdownsByDate.keys()),
  );

  let remainingAmount = availableAmount;

  // Waterfall through each month chronologically
  for (const date of sortedDates) {
    if (remainingAmount <= 0.01) break;

    const monthBreakdowns = breakdownsByDate.get(date)!;

    // Calculate buckets with capacity for this month
    const buckets = calculateBucketsWithCapacity(
      monthBreakdowns,
      existingAllocationsMap,
    );

    // Calculate total remaining capacity for this month
    const totalMonthCapacity = buckets.reduce(
      (sum, b) => sum + b.remainingCapacity,
      0,
    );

    if (totalMonthCapacity <= 0) {
      // Month already fully allocated, skip
      continue;
    }

    // Allocate using waterfall mode (proportionally within the month)
    const amountToAllocate = Math.min(remainingAmount, totalMonthCapacity);
    const monthAllocations = distributeAllocations(
      buckets,
      amountToAllocate,
      'distribute-evenly', // Distribute evenly within the month
    );

    // Merge month allocations into main allocations
    Object.assign(newAllocations, monthAllocations);

    // Update remaining amount
    const monthTotal = Object.values(monthAllocations).reduce(
      (sum, amt) => sum + amt,
      0,
    );
    remainingAmount -= monthTotal;
  }

  // Normalize to fix any rounding differences
  const allBuckets = calculateBucketsWithCapacity(
    filteredBreakdowns,
    existingAllocationsMap,
  );
  const normalizedAllocations = normalizeAllocations(
    newAllocations,
    availableAmount,
    allBuckets,
  );

  return normalizedAllocations;
}

/**
 * Allocate by Division: user selects divisions, then waterfall chronologically through dates
 */
export function allocateByDivision(
  params: AllocateByDivisionParams,
): AllocationState {
  const {
    calculatedBreakdowns,
    dates,
    availableAmount,
    globalAllocations,
    paymentId,
    selectedDivisionIds,
  } = params;

  // Reset allocations first
  const newAllocations: AllocationState = {};

  if (
    calculatedBreakdowns.length === 0 ||
    dates.length === 0 ||
    selectedDivisionIds.length === 0
  ) {
    return newAllocations;
  }

  const selectedDivisionIdsSet = new Set(selectedDivisionIds);

  // Filter breakdowns to selected divisions and dates in the selected range
  const dateSet = new Set(dates);
  const filteredBreakdowns = calculatedBreakdowns.filter(
    (bd) => dateSet.has(bd.date) && selectedDivisionIdsSet.has(bd.divisionId),
  );

  if (filteredBreakdowns.length === 0) {
    return newAllocations;
  }

  // Build map of existing allocations (excluding current payment)
  const existingAllocationsMap = buildExistingAllocationsMap(
    globalAllocations,
    paymentId,
  );

  // Group breakdowns by division, then by date
  const breakdownsByDivisionAndDate = new Map<
    string,
    Map<string, typeof filteredBreakdowns>
  >();
  filteredBreakdowns.forEach((bd) => {
    if (!breakdownsByDivisionAndDate.has(bd.divisionId)) {
      breakdownsByDivisionAndDate.set(bd.divisionId, new Map());
    }
    const divisionMap = breakdownsByDivisionAndDate.get(bd.divisionId)!;
    if (!divisionMap.has(bd.date)) {
      divisionMap.set(bd.date, []);
    }
    divisionMap.get(bd.date)!.push(bd);
  });

  let remainingAmount = availableAmount;

  // Process each selected division in the order they were selected
  for (const divisionId of selectedDivisionIds) {
    if (remainingAmount <= 0.01) break;

    const divisionDateMap = breakdownsByDivisionAndDate.get(divisionId);
    if (!divisionDateMap) continue;

    // Sort dates chronologically for this division
    const sortedDates = sortDatesChronologically(
      Array.from(divisionDateMap.keys()),
    );

    // Waterfall through dates chronologically for this division
    for (const date of sortedDates) {
      if (remainingAmount <= 0.01) break;

      const dateBreakdowns = divisionDateMap.get(date)!;

      // Calculate buckets with capacity for this date
      const buckets = calculateBucketsWithCapacity(
        dateBreakdowns,
        existingAllocationsMap,
      );

      // Calculate total remaining capacity for this date
      const totalDateCapacity = buckets.reduce(
        (sum, b) => sum + b.remainingCapacity,
        0,
      );

      if (totalDateCapacity <= 0) {
        // Date already fully allocated, skip
        continue;
      }

      // Allocate using distribute-evenly mode within the date
      const amountToAllocate = Math.min(remainingAmount, totalDateCapacity);
      const dateAllocations = distributeAllocations(
        buckets,
        amountToAllocate,
        'distribute-evenly',
      );

      // Merge date allocations into main allocations
      Object.assign(newAllocations, dateAllocations);

      // Update remaining amount
      const dateTotal = Object.values(dateAllocations).reduce(
        (sum, amt) => sum + amt,
        0,
      );
      remainingAmount -= dateTotal;
    }
  }

  // Normalize to fix any rounding differences
  const allBuckets = calculateBucketsWithCapacity(
    filteredBreakdowns,
    existingAllocationsMap,
  );
  const normalizedAllocations = normalizeAllocations(
    newAllocations,
    availableAmount,
    allBuckets,
  );

  return normalizedAllocations;
}

/**
 * Allocate by Rule: user selects rules, then waterfall chronologically through dates
 */
export function allocateByRule(params: AllocateByRuleParams): AllocationState {
  const {
    calculatedBreakdowns,
    dates,
    availableAmount,
    globalAllocations,
    paymentId,
    selectedRuleIds,
  } = params;

  // Reset allocations first
  const newAllocations: AllocationState = {};

  if (
    calculatedBreakdowns.length === 0 ||
    dates.length === 0 ||
    selectedRuleIds.length === 0
  ) {
    return newAllocations;
  }

  const selectedRuleIdsSet = new Set(selectedRuleIds);

  // Filter breakdowns to selected rules and dates in the selected range
  const dateSet = new Set(dates);
  const filteredBreakdowns = calculatedBreakdowns.filter(
    (bd) => dateSet.has(bd.date) && selectedRuleIdsSet.has(bd.ruleId),
  );

  if (filteredBreakdowns.length === 0) {
    return newAllocations;
  }

  // Build map of existing allocations (excluding current payment)
  const existingAllocationsMap = buildExistingAllocationsMap(
    globalAllocations,
    paymentId,
  );

  // Group breakdowns by rule, then by date
  const breakdownsByRuleAndDate = new Map<
    string,
    Map<string, typeof filteredBreakdowns>
  >();
  filteredBreakdowns.forEach((bd) => {
    if (!breakdownsByRuleAndDate.has(bd.ruleId)) {
      breakdownsByRuleAndDate.set(bd.ruleId, new Map());
    }
    const ruleMap = breakdownsByRuleAndDate.get(bd.ruleId)!;
    if (!ruleMap.has(bd.date)) {
      ruleMap.set(bd.date, []);
    }
    ruleMap.get(bd.date)!.push(bd);
  });

  let remainingAmount = availableAmount;

  // Process each selected rule in the order they were selected
  for (const ruleId of selectedRuleIds) {
    if (remainingAmount <= 0.01) break;

    const ruleDateMap = breakdownsByRuleAndDate.get(ruleId);
    if (!ruleDateMap) continue;

    // Sort dates chronologically for this rule
    const sortedDates = sortDatesChronologically(
      Array.from(ruleDateMap.keys()),
    );

    // Waterfall through dates chronologically for this rule
    for (const date of sortedDates) {
      if (remainingAmount <= 0.01) break;

      const dateBreakdowns = ruleDateMap.get(date)!;

      // Calculate buckets with capacity for this date
      const buckets = calculateBucketsWithCapacity(
        dateBreakdowns,
        existingAllocationsMap,
      );

      // Calculate total remaining capacity for this date
      const totalDateCapacity = buckets.reduce(
        (sum, b) => sum + b.remainingCapacity,
        0,
      );

      if (totalDateCapacity <= 0) {
        // Date already fully allocated, skip
        continue;
      }

      // Allocate using distribute-evenly mode within the date
      const amountToAllocate = Math.min(remainingAmount, totalDateCapacity);
      const dateAllocations = distributeAllocations(
        buckets,
        amountToAllocate,
        'distribute-evenly',
      );

      // Merge date allocations into main allocations
      Object.assign(newAllocations, dateAllocations);

      // Update remaining amount
      const dateTotal = Object.values(dateAllocations).reduce(
        (sum, amt) => sum + amt,
        0,
      );
      remainingAmount -= dateTotal;
    }
  }

  // Normalize to fix any rounding differences
  const allBuckets = calculateBucketsWithCapacity(
    filteredBreakdowns,
    existingAllocationsMap,
  );
  const normalizedAllocations = normalizeAllocations(
    newAllocations,
    availableAmount,
    allBuckets,
  );

  return normalizedAllocations;
}
